/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { MessageError, retrieveAndValidateFirmware, WebUsbConnectorCCH } from 'web-usb-common';
import { Diagnostics } from 'web-audio-diagnostics';
import { AuxiliaryOutputsState, AuxliaryOutputsNames, DeviceData, WebUsbModuleVersion } from 'web-usb-module';

const INPUT_MESSAGE_SIZE = 64;
const EVENT_MESSAGE_OPCODE = 0x01;
const LOG_MESSAGE_OPCODE = 0x03;
const DEFAULT_BULK_WRITE_TIMEOUT = 10000;
const FPGA_RETRIEVE_STATUS_TIMEOUT = 10000;
export class WebUsbManager {

    callbackSuccess: (data: DeviceData) => void;
    callbackError: (error: MessageError) => void;
    deviceConnected: boolean;
    private readonly webUsbConnector: WebUsbConnectorCCH;
    private readonly diagnostics;
    private readonly auxiliaryOutputs;
    private readonly deviceData;
    private lastReceivedMessage: any;

    constructor(diagnostics = Diagnostics.getInstance()) {
        this.diagnostics = diagnostics;
        this.callbackSuccess = undefined;
        this.callbackError = undefined;
        this.deviceConnected = false;
        this.lastReceivedMessage = null;
        this.deviceData = new DeviceData();
        console.log('WebUsbModule::version = ', WebUsbModuleVersion);
        this.auxiliaryOutputs = new AuxiliaryOutputsState();
        this.webUsbConnector = WebUsbConnectorCCH.getInstance(this.diagnostics);
    }
    /**
     * Initialize WebUsb module processing
     *
     * @param callbackSuccess Callback function called when intialization successfully
     * @param callbackError Callback function called when intialization failed or some error occurs during work
     */
    init(callbackSuccess: (data: DeviceData) => void, callbackError: (error: MessageError) => void) {
        this.callbackSuccess = callbackSuccess;
        this.callbackError = callbackError;
    }

    /**
     * Returns auxiliary port states
     */
    getAuxiliaryOutputs() {
        return this.auxiliaryOutputs;
    }
    /**
     * Stop processing any WebUSB events and disconnect device
     */
    stop() {
        this.deviceConnected = false;
        return this.webUsbConnector.stop();
    }
    async connectToDevice() {
        if (!this.deviceConnected) {
            await this.webUsbConnector.connectNoCheck().catch((error) => {
                this.callbackError({ name: 'code: 505', message: `USB Device connection error: ${(error as Error).name}: ${(error as Error).message}` });
                throw (error);
            });
            this.deviceConnected = true;
        }
    }
    finalizeConnection() {
        this.lastReceivedMessage = null;
        this.readInterruptLoop();
    }
    /**
     * Disconnects device
     */
    disconnect() {
        this.handleInputStates(new Uint8Array([1, 0, 0]));
        this.deviceConnected = false;
        return this.webUsbConnector.disconnect()
            .catch((error) => console.log('disconnect error = ', error));

    }
    validateFirmware() {
        return retrieveAndValidateFirmware(this.webUsbConnector, DEFAULT_BULK_WRITE_TIMEOUT, FPGA_RETRIEVE_STATUS_TIMEOUT, () => {return;}, this.getCallbackError());
    }
    async readInterruptLoop() {
        while (this.deviceConnected) {
            try {
                const result = await this.webUsbConnector.receive('endpointInInterrupt', INPUT_MESSAGE_SIZE);
                if (this.checkIfMsgChanged(result) && this.callbackSuccess != null) {
                    if (result[0] === EVENT_MESSAGE_OPCODE) {
                        const input = this.handleInputStates(result);
                        this.callbackSuccess(input);
                    }
                    else if (result[0] === LOG_MESSAGE_OPCODE) {
                        const errorLog = this.parseLog(result);
                        this.callbackError( { name: 'code: 501', message: `USB Device error: ${errorLog}` } );
                    }
                    else {
                        const errorLog = result[0].toString();
                        this.callbackError( { name: 'code: 502', message: `USB Device error: Unknown opcode: ${errorLog}` } );
                    }
                }
            }
            catch (error) {
                if ((error as Error).name === 'NotFoundError') {
                    this.deviceConnected = false;
                }
                this.callbackError( { name: 'code: 503', message: `USB Device error: ${(error as Error).name}: ${(error as Error).message}` } );
            }
        }
    }
    checkIfMsgChanged(msg: Uint8Array) {
        let result = false;
        if (this.lastReceivedMessage === null) {
            result = true;
        }
        else if (msg.byteLength !== this.lastReceivedMessage.byteLength) {
            result = true;
        }
        else if (!msg.every((value, index) => value === this.lastReceivedMessage[index])) {
            result = true;
        }
        this.lastReceivedMessage = msg;
        return result;
    }
    /**
     * Sends RPI keep alive packets
     */
    sendRPIKeepAlive() {
        const buf = new ArrayBuffer(3);
        const dataView = new DataView(buf);
        dataView.setUint8(0, 0x01);
        dataView.setUint16(1, 0x0100);
        return this.webUsbConnector.sendData('endpointOutInterrupt', dataView, 'sending EPI Keep Alive')
            .catch((error: Error) => this.sendError({ name: error.name, message: error.message }));
    }
    /**
     * Send value to auxiliary port
     *
     * @param auxName auxiliary port which state is to be changed
     * @param value value to sent
     */
    sendOutputs(auxName: AuxliaryOutputsNames, value: boolean) {
        this.auxiliaryOutputs.changeState(auxName, value);
        const msgBody = this.auxiliaryOutputs.getStateBitmap();
        const buf = new ArrayBuffer(3);
        const dataView = new DataView(buf);
        dataView.setUint8(0, 0x02);
        dataView.setUint16(1, msgBody, true);
        return this.webUsbConnector.sendData('endpointOutInterrupt', dataView, 'sending outputs')
            .catch((error: Error) => {
                throw {
                    code: 507,
                    description: 'USB Device error: ' + error.name + ' - ' + error.message,
                    details: auxName
                };
            });
    }
    private updateDeviceDataStatuses(data: Uint8Array) {
        const deviceEventData = new Uint16Array(data.slice(1, 3).reverse().buffer);
        this.deviceData.DESKTOP_MIC_JS.state = !!(deviceEventData[0] & 0x0100);
        this.deviceData.DESKTOP_MIC_PTT.state = !!(deviceEventData[0] & 0x0200);
        this.deviceData.DESKTOP_MIC_MONITOR.state = !!(deviceEventData[0] & 0x0400);
        this.deviceData.HEADSET1_JS.state = !!(deviceEventData[0] & 0x0800);
        this.deviceData.HEADSET1_PTT.state = !!(deviceEventData[0] & 0x1000);
        this.deviceData.HEADSET2_JS.state = !!(deviceEventData[0] & 0x2000);
        this.deviceData.HEADSET2_PTT.state = !!(deviceEventData[0] & 0x4000);
        this.deviceData.EPI_JS.state = !!(deviceEventData[0] & 0x8000);
        this.deviceData.EPI_OFFHOOK.state = !!(deviceEventData[0] & 0x0001);
        this.deviceData.FOOTSWITCH_PTT.state = !!(deviceEventData[0] & 0x0002);
        this.deviceData.FOOTSWITCH_MONITOR.state = !!(deviceEventData[0] & 0x0004);
        this.deviceData.EPE_PTT.state = !!(deviceEventData[0] & 0x0008);
        this.deviceData.DESKTOP_MIC_PTT.hidden = !this.deviceData.DESKTOP_MIC_JS.state;
        this.deviceData.DESKTOP_MIC_MONITOR.hidden = !this.deviceData.DESKTOP_MIC_JS.state;
        this.deviceData.HEADSET1_PTT.hidden = !this.deviceData.HEADSET1_JS.state;
        this.deviceData.HEADSET2_PTT.hidden = !this.deviceData.HEADSET2_JS.state;
        return this.deviceData;
    }
    getDeviceDataStatuses() {
        return this.deviceData;
    }

    private parseLog(data: Uint8Array) {
        const size = data[1];
        const startLogIndex = 2;
        const logBuffer = data.slice(startLogIndex, startLogIndex + size);
        let result = '';
        for (let i = 0; i < size; ++i) {
            result += String.fromCharCode(logBuffer[i]);
        }
        return result;
    }
    protected handleInputStates(data: Uint8Array) {
        return this.updateDeviceDataStatuses(data);
    }
    sendError(error: Error) {
        if (this.callbackError != null) {
            this.callbackError(error);
        }
        else {
            console.log('Error name: ' + error.name + ', error message: ' + error.message);
        }
    }
    getCallbackError() {
        return (functionArg: any, nameArg: any, messageArg: any) => {
            this.sendError({ name: nameArg, message: messageArg });
        };
    }
}
